// Wording and shapes for the MeshMapper region outlines block (Settings, Regions). Pure; covered by
// tests/meshmapperRegions.test.mjs. The data comes from GET /api/meshmapper/regions/status; nothing here calls MeshMapper.

export const OUTLINES_MIN_HOURS = 6;
export const OUTLINES_MAX_HOURS = 168;
export const OUTLINES_MAX_COUNTRIES = 5;

export const OUTLINES_INTRO =
  "Region outlines come from MeshMapper's official API, using the MeshMapper key set under MeshMapper. MeshMapper lists only the regions your key covers.";

/** What GET /api/meshmapper/regions/status returns. */
export interface RegionOutlinesStatus {
  enabled: boolean;
  refresh_hours: number;
  countries: string[];
  key_set: boolean;
  state: string;
  last_attempt_at: string | null;
  last_ok_at: string | null;
  last_result: string | null;
  last_error: string | null;
  last_requests: number;
  regions_loaded: number;
  regions_frozen: number;
  next_refresh_at: string | null;
}

/** Where the refresh is, in a few words. */
export function outlinesStateText(state: string): string {
  switch (state) {
    case "off":
      return "Switched off";
    case "no_key":
      return "No MeshMapper key set";
    case "blocked":
      return "Stopped: check the key and the countries";
    case "backoff":
      return "Waiting before trying again";
    case "waiting":
      return "Waiting for the next refresh";
    case "due":
      return "Refreshing now";
    default:
      return state;
  }
}

/** The result of the last refresh, in a few words. */
export function outlinesResultText(result: string | null | undefined): string {
  switch (result) {
    case "updated":
      return "Updated";
    case "unchanged":
      return "Checked, nothing new";
    case "backoff":
      return "Asked to wait, will try again";
    case "rejected":
      return "Key or countries not accepted";
    case "error":
      return "Failed, the last outlines were kept";
    default:
      return "Not refreshed yet";
  }
}

/** The refresh time as typed: a whole number of hours from 6 to 168, else null. */
export function parseOutlineHours(text: string): number | null {
  if (!/^\s*\d+\s*$/.test(text)) return null;
  const n = parseInt(text, 10);
  return n >= OUTLINES_MIN_HOURS && n <= OUTLINES_MAX_HOURS ? n : null;
}

/** The countries as typed ("US, CA"): capital two-letter codes without repeats, or null when something is not a code
 *  (or there are none, or more than five). */
export function parseCountries(text: string): string[] | null {
  const parts = text.split(/[\s,]+/).map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0 || parts.length > OUTLINES_MAX_COUNTRIES) return null;
  const out: string[] = [];
  for (const p of parts) {
    if (!/^[A-Za-z]{2}$/.test(p)) return null;
    const up = p.toUpperCase();
    if (!out.includes(up)) out.push(up);
  }
  return out;
}

export function countriesText(countries: string[]): string {
  return countries.join(", ");
}
